// Verkopersvergoeding op maat, per energielabel.
//
// Elk energielabel rekent zijn verkopersvergoeding anders af. De algemene
// Revenue-pagina toont de vergoeding zoals Energy Together ze berekent; deze
// module bevat de afwijkende methodieken die een partner met ons afgesproken
// heeft. Servolt Energie is de eerste.
//
// Alles hier is pure rekenwerk zonder React of DOM, zodat de methodiek in
// customFee.test.js na te rekenen valt — bij geldbedragen is dat geen luxe.

// ── Servolt Energie ──────────────────────────────────────────────────────────
// Vaste vergoeding per geactiveerde EAN, onafhankelijk van de contractduur maar
// afhankelijk van het geschatte jaarverbruik (Est. Offtake). Elektriciteit en gas
// hebben eigen grenzen. Daarbovenop één keer een bonus als de klant na twaalf
// maanden nog actief is; verlengingen en productwissels tellen daarbij als één
// doorlopende periode.
export const SERVOLT_DEFAULT_TARIEVEN = {
  // Grenzen in MWh geschat jaarverbruik. `t1` scheidt categorie 1 van 2, `t2`
  // categorie 2 van 3. De grens zelf hoort bij de HOGERE categorie: precies
  // 3 MWh elektriciteit is dus categorie 2 ("tussen 3 en 5").
  elT1: 3,
  elT2: 5,
  ngT1: 7,
  ngT2: 10,
  // Vergoeding per categorie, in euro per geactiveerde EAN.
  cat1: 40,
  cat2: 50,
  cat3: 60,
  // Eenmalige bonus per EAN die twaalf maanden na de start nog actief is.
  bonus12m: 25,
};

// Alleen deze sleutels worden bewaard/gelezen; onbekende velden uit de database
// worden genegeerd zodat een oude of handmatig aangepaste rij nooit iets stuks
// maakt. Niet-numerieke of negatieve waarden vallen terug op de standaard.
export const SERVOLT_TARIEF_VELDEN = Object.keys(SERVOLT_DEFAULT_TARIEVEN);

export function normaliseerTarieven(raw) {
  const uit = { ...SERVOLT_DEFAULT_TARIEVEN };
  for (const veld of SERVOLT_TARIEF_VELDEN) {
    const v = Number(raw?.[veld]);
    if (Number.isFinite(v) && v >= 0) uit[veld] = v;
  }
  // Een omgekeerde grens (t2 onder t1) zou categorie 2 laten verdwijnen; dat is
  // bijna zeker een typfout, dus we zetten ze recht in plaats van stil te rekenen.
  if (uit.elT2 < uit.elT1) uit.elT2 = uit.elT1;
  if (uit.ngT2 < uit.ngT1) uit.ngT2 = uit.ngT1;
  return uit;
}

// 'EL' of 'NG'. Alles wat geen gas is behandelen we als elektriciteit; dat is de
// veruit grootste groep en een leeg market-veld hoort niet in een eigen categorie.
export const marktVan = (o) => (String(o?.market || '').toUpperCase() === 'NG' ? 'NG' : 'EL');

export const estMwh = (o) => (Number(o?.estOfftakeKwh) || 0) / 1000;

// Categorie 1, 2 of 3 op basis van het geschatte jaarverbruik.
export function categorieVoorOrder(o, tarieven = SERVOLT_DEFAULT_TARIEVEN) {
  const t = normaliseerTarieven(tarieven);
  const mwh = estMwh(o);
  const [t1, t2] = marktVan(o) === 'NG' ? [t.ngT1, t.ngT2] : [t.elT1, t.elT2];
  if (mwh < t1) return 1;
  if (mwh <= t2) return 2;
  return 3;
}

export function bedragVoorCategorie(cat, tarieven = SERVOLT_DEFAULT_TARIEVEN) {
  const t = normaliseerTarieven(tarieven);
  return cat === 3 ? t.cat3 : cat === 2 ? t.cat2 : t.cat1;
}

// ── Ketens: verlenging en wissel zijn één periode ────────────────────────────
// De startdatum van een order: effectief als die er is, anders de geplande
// leveringsstart. Dat is dezelfde regel als elders in het platform.
export const orderStartDatum = (o) => {
  const s = o?.effectiveStartDate || o?.deliveryStartDate || '';
  return s ? String(s).slice(0, 10) : '';
};
const orderEindDatum = (o) => {
  const e = o?.effectiveEndDate || o?.deliveryEndDate || '';
  return e ? String(e).slice(0, 10) : '';
};
export const maandVan = (datum) => (datum ? String(datum).slice(0, 7) : '');

const DAG_MS = 86400000;
const dagen = (a, b) => Math.round((new Date(b) - new Date(a)) / DAG_MS);

// Groepeer de orders per EAN en rijg ze aaneen tot doorlopende periodes: een
// order die aansluit op (of overlapt met) het einde van de vorige hoort bij
// dezelfde keten. Zo telt een verlenging of productwissel niet als een nieuwe
// klantperiode. Losse gaten van meer dan een dag breken de keten wél.
export function ketensPerEan(orders) {
  const perGsrn = new Map();
  for (const o of orders || []) {
    const g = o?.connectionPointGSRN;
    if (!g || !orderStartDatum(o)) continue;
    const a = perGsrn.get(g) || [];
    a.push(o);
    perGsrn.set(g, a);
  }
  const uit = new Map();
  for (const [gsrn, lijst] of perGsrn) {
    const gesorteerd = [...lijst].sort((a, b) => orderStartDatum(a).localeCompare(orderStartDatum(b)));
    const ketens = [];
    for (const o of gesorteerd) {
      const start = orderStartDatum(o);
      const eind = orderEindDatum(o);
      const huidig = ketens[ketens.length - 1];
      // Een keten zonder einddatum loopt nog: alles wat daarna start hoort erbij.
      if (huidig && (!huidig.eind || dagen(huidig.eind, start) <= 1)) {
        huidig.orders.push(o);
        if (!eind || (huidig.eind && eind > huidig.eind)) huidig.eind = eind;
      } else {
        ketens.push({ orders: [o], start, eind });
      }
    }
    uit.set(gsrn, ketens);
  }
  return uit;
}

const ketenVanOrder = (ketens, o) => (ketens || []).find(k => k.orders.includes(o)) || null;

// Verdient deze EAN de twaalfmaandenbonus? De keten waar het order in zit moet
// twaalf maanden na haar START nog lopen, en die twaalf maanden moeten al
// voorbij zijn — anders is het nog niet verdiend, alleen nog niet uitgesloten.
export function bonusVerdiend(keten, vandaag = new Date()) {
  if (!keten || !keten.start) return false;
  const grens = new Date(keten.start);
  grens.setMonth(grens.getMonth() + 12);
  if (grens > vandaag) return false;               // twaalf maanden nog niet om
  if (!keten.eind) return true;                    // loopt nog → actief
  return new Date(keten.eind) >= grens;            // liep door tot voorbij de grens
}

// ── De tabel "Vergoeding aantal EAN" ─────────────────────────────────────────
// Eén rij per EAN die in de gekozen maand gestart is. `alleOrders` is de volledige
// (rol-gescopete) orderlijst — nodig om de keten van een EAN te kennen, ook als de
// verlenging buiten de gekozen maand valt.
//
// opties:
//   maand              'YYYY-MM' — verplicht, anders geen rijen
//   verkoper           optioneel; filtert op salesperson
//   tarieven           zie SERVOLT_DEFAULT_TARIEVEN
//   verlengingenTellen of een verlenging óók de activatievergoeding verdient
//   vandaag            testbaarheid
export function bouwEanRijen(alleOrders, opties = {}) {
  const { maand, verkoper = null, tarieven = SERVOLT_DEFAULT_TARIEVEN,
    verlengingenTellen = true, vandaag = new Date() } = opties;
  if (!maand) return [];
  const t = normaliseerTarieven(tarieven);
  const ketens = ketensPerEan(alleOrders);

  const rijen = [];
  // Per EAN maar één rij per maand: twee orders op dezelfde aansluiting in
  // dezelfde maand (bv. een correctie) is één activatie, geen twee vergoedingen.
  const gezien = new Set();
  for (const o of alleOrders || []) {
    const start = orderStartDatum(o);
    if (maandVan(start) !== maand) continue;
    const gsrn = o.connectionPointGSRN;
    if (!gsrn || gezien.has(gsrn)) continue;
    if (verkoper && (o.salesperson || '') !== verkoper) continue;
    gezien.add(gsrn);

    const keten = ketenVanOrder(ketens.get(gsrn), o);
    const eersteVanKeten = !!keten && keten.orders[0] === o;
    const cat = categorieVoorOrder(o, t);
    const teltMee = eersteVanKeten || verlengingenTellen;
    const activatie = teltMee ? bedragVoorCategorie(cat, t) : 0;
    // De bonus hangt aan de KETEN, dus hij hoort bij de rij waar die keten begint.
    const bonus = eersteVanKeten && bonusVerdiend(keten, vandaag) ? t.bonus12m : 0;
    rijen.push({
      gsrn,
      klant: o.customer || '',
      klantnummer: o.customerReference || '',
      verkoper: o.salesperson || '',
      verkoopteam: o.salespersonTeam || '',
      markt: marktVan(o),
      segment: (o.segment || '').toUpperCase(),
      start,
      soort: eersteVanKeten ? 'Nieuw' : 'Verlenging',
      estMwh: estMwh(o),
      categorie: cat,
      activatie,
      bonus,
      totaal: activatie + bonus,
    });
  }
  rijen.sort((a, b) => a.start.localeCompare(b.start) || a.klant.localeCompare(b.klant));
  return rijen;
}

// Samenvatting per categorie + totaalrij, voor het tweede tabelletje eronder.
export function feePerCategorie(rijen, tarieven = SERVOLT_DEFAULT_TARIEVEN) {
  const t = normaliseerTarieven(tarieven);
  const leeg = () => ({ aantal: 0, mwh: 0, activatie: 0, bonus: 0, totaal: 0 });
  const per = { 1: leeg(), 2: leeg(), 3: leeg() };
  for (const r of rijen || []) {
    const p = per[r.categorie] || (per[r.categorie] = leeg());
    p.aantal += 1;
    p.mwh += r.estMwh;
    p.activatie += r.activatie;
    p.bonus += r.bonus;
    p.totaal += r.totaal;
  }
  const rows = [1, 2, 3].map(cat => ({
    categorie: cat,
    tarief: bedragVoorCategorie(cat, t),
    ...per[cat],
  }));
  const totaal = rows.reduce((a, r) => ({
    aantal: a.aantal + r.aantal, mwh: a.mwh + r.mwh,
    activatie: a.activatie + r.activatie, bonus: a.bonus + r.bonus, totaal: a.totaal + r.totaal,
  }), leeg());
  return { rows, totaal };
}

// De maand vóór de gekozen maand — de facturen die bij die maand horen in de
// tabel "Vergoeding gefactureerde kWh".
export function vorigeMaand(maand) {
  if (!/^\d{4}-\d{2}$/.test(String(maand || ''))) return '';
  const [j, m] = maand.split('-').map(Number);
  const d = new Date(Date.UTC(j, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

// ── Evident Energie ──────────────────────────────────────────────────────────
// Commissie per GEFACTUREERDE MWh, en niet per geactiveerde EAN zoals bij
// Servolt. Twee prijszettingen naast elkaar:
//
//   1. RESIDENTIEEL + PROFESSIONEEL — één vast bedrag per MWh, apart voor
//      elektriciteit en gas.
//   2. CUSTOM PRICING (AMR/MMR) — trapsgewijs op de verkoopformule: hoe hoger de
//      opslag (majoration) op de aansluiting, hoe hoger de commissie per MWh.
//
// De scheiding tussen die twee ligt bij het klantensegment ÉN de meetmethode:
// alleen een Custom-aansluiting die per kwartier of maand gemeten wordt (AMR of
// MMR) valt onder de custom pricing. Al de rest — inclusief een Custom-klant met
// een gewone meter — rekent aan het vaste tarief af.
export const EVIDENT_DEFAULT_TARIEVEN = {
  // Vast tarief per MWh (residentieel + professioneel).
  resEl: 12,
  resNg: 7,
  // Custom pricing: per trap een DREMPEL (de opslag vanaf waar ze geldt) en het
  // TARIEF in euro per MWh. Trap 1 is de hoogste opslag. Onder de laagste drempel
  // is er geen commissie — dat is een bewuste keuze en geen ontbrekende trap.
  custElMaj1: 25, custElTar1: 14,
  custElMaj2: 20, custElTar2: 12,
  custElMaj3: 15, custElTar3: 7,
  custElMaj4: 10, custElTar4: 2,
  custNgMaj1: 15, custNgTar1: 8,
  custNgMaj2: 13, custNgTar2: 7,
  custNgMaj3: 10, custNgTar3: 4,
  custNgMaj4: 8, custNgTar4: 2,
  // Injectie elektriciteit (Maj Injection EL) — dezelfde trappen als de afname van
  // elektriciteit, tot er iets anders afgesproken wordt. De opslag op injectie
  // staat in de data NEGATIEF (contractdefault −25); er wordt met de absolute
  // waarde getoetst, zoals overal in het platform (calcELDetailSB).
  custInjMaj1: 25, custInjTar1: 14,
  custInjMaj2: 20, custInjTar2: 12,
  custInjMaj3: 15, custInjTar3: 7,
  custInjMaj4: 10, custInjTar4: 2,
  // Doelstellingen voor de pagina "Commissie overzicht", in euro commissie per
  // KALENDERJAAR. 0 = geen doel ingesteld; dan toont die pagina geen voortgang in
  // plaats van een percentage van nul.
  doelJaarLabel: 0,
  doelJaarVerkoper: 0,
};

export const EVIDENT_TARIEF_VELDEN = Object.keys(EVIDENT_DEFAULT_TARIEVEN);

// Zelfde regel als bij Servolt: onbekende velden negeren, ongeldige of negatieve
// waarden vallen terug op de standaard.
export function normaliseerEvident(raw) {
  const uit = { ...EVIDENT_DEFAULT_TARIEVEN };
  for (const veld of EVIDENT_TARIEF_VELDEN) {
    const v = Number(raw?.[veld]);
    if (Number.isFinite(v) && v >= 0) uit[veld] = v;
  }
  return uit;
}

// De drie trappenreeksen, hoogste drempel eerst. `soort` = 'El' | 'Ng' | 'Inj'.
// Sorteren is geen luxe: een beheerder kan de drempels door elkaar zetten, en dan
// zou een lineaire lees-volgorde stilzwijgend het verkeerde tarief kiezen.
export function evidentTrappen(tarieven, soort) {
  const t = normaliseerEvident(tarieven);
  return [1, 2, 3, 4]
    .map((i) => ({ drempel: t[`cust${soort}Maj${i}`], tarief: t[`cust${soort}Tar${i}`] }))
    .sort((a, b) => b.drempel - a.drempel);
}

// Het tarief dat bij een opslag hoort: de hoogste trap waarvan de drempel bereikt
// is. Onder de laagste drempel (of zonder opslag) → 0.
export function evidentTariefVoorMaj(maj, trappen) {
  const m = Number(maj);
  if (!Number.isFinite(m)) return 0;
  for (const trap of trappen || []) if (m >= trap.drempel) return trap.tarief;
  return 0;
}

// Meetmethodes die de ELEKTRICITEITSkant onder de custom pricing brengen.
export const EVIDENT_CUSTOM_METERS = new Set(['AMR', 'MMR']);

export const EVIDENT_GROEPEN = ['res', 'custom'];
export const EVIDENT_GROEP_LABEL = {
  res: 'Residentieel + professioneel',
  custom: 'Custom pricing (AMR EL/MMR EL/Custom GAS)',
};

// In welke prijszetting valt deze kant van een aansluiting?
//
// Elektriciteit (afname én injectie): Custom-segment ÉN een AMR- of MMR-meter.
// Een Custom-klant met een gewone meter rekent aan het vaste tarief af.
//
// GAS: het Custom-segment volstaat, de meetmethode doet er NIET toe. Dat is een
// bewuste afwijking (gevraagd 18-08-2026): de meetmethode die we hebben is die
// van de elektriciteitskant — voor een gasaansluiting zegt ze niets, en een
// Custom-gasklant zou anders op het residentiële tarief belanden alleen omdat
// zijn elektriciteitsmeter geen AMR/MMR is.
export function evidentGroep(segment, meterMethod, energie = 'EL') {
  const isCustom = String(segment || '').toUpperCase().includes('CUSTOM');
  if (!isCustom) return 'res';
  if (String(energie).toUpperCase() === 'NG') return 'custom';
  const meter = String(meterMethod || '').trim().toUpperCase();
  return EVIDENT_CUSTOM_METERS.has(meter) ? 'custom' : 'res';
}

// De opslag op INJECTIE staat negatief in de data (contractdefault −25 wanneer ze
// leeg is). De trappen praten over de hoogte van de opslag, dus we toetsen op de
// absolute waarde — net als calcELDetailSB in de rest van het platform.
export const EVIDENT_INJ_MAJ_DEFAULT = -25;
export const evidentInjMaj = (maj) => {
  const n = Number(maj);
  return Math.abs(Number.isFinite(n) && n !== 0 ? n : EVIDENT_INJ_MAJ_DEFAULT);
};

// Commissie voor ÉÉN kant van een regel: elektriciteit (afname), injectie of gas.
// `energie` = 'EL' | 'INJ' | 'NG'. Geen aansluiting aan die kant (kwh null) →
// null, zodat de tabel een lege cel kan tonen i.p.v. een nul die "gemeten nul"
// suggereert.
export function evidentZijde({ kwh, segment, meterMethod, maj, energie = 'EL', tarieven }) {
  if (kwh == null || kwh === '') return null;
  const t = normaliseerEvident(tarieven);
  const soortNaam = String(energie).toUpperCase();
  const groep = evidentGroep(segment, meterMethod, soortNaam);
  const mwh = (Number(kwh) || 0) / 1000;
  const soort = soortNaam === 'NG' ? 'Ng' : soortNaam === 'INJ' ? 'Inj' : 'El';
  const vast = soortNaam === 'NG' ? t.resNg : t.resEl;
  const opslag = soortNaam === 'INJ' ? evidentInjMaj(maj) : maj;
  const tariefPerMwh = groep === 'custom' ? evidentTariefVoorMaj(opslag, evidentTrappen(t, soort)) : vast;
  return { groep, mwh, tariefPerMwh, commissie: mwh * tariefPerMwh };
}

// De EAN-regels met hun commissie erbij. `rijen` zijn de gebundelde regels uit
// eanVolume.js (aangevuld met de injectie uit de self bills), al teruggebracht
// tot de velden die hier tellen — zo blijft dit rekenwerk los van de vorm van de
// facturenfeed.
//
// Drie kanten per regel: EL-afname, EL-injectie en gas. De injectie volgt het
// segment en de meter van de elektriciteitskant (het is dezelfde aansluiting),
// tenzij de self bill zelf een segment draagt.
export function evidentEanRijen(rijen, tarieven = EVIDENT_DEFAULT_TARIEVEN) {
  const t = normaliseerEvident(tarieven);
  return (rijen || []).map((r) => {
    const el = evidentZijde({ kwh: r.elKwh, segment: r.elSegment, meterMethod: r.meterMethod, maj: r.elMaj, energie: 'EL', tarieven: t });
    const inj = evidentZijde({ kwh: r.injKwh, segment: r.injSegment || r.elSegment, meterMethod: r.meterMethod, maj: r.injMaj, energie: 'INJ', tarieven: t });
    const ng = evidentZijde({ kwh: r.ngKwh, segment: r.ngSegment, meterMethod: r.meterMethod, maj: r.ngMaj, energie: 'NG', tarieven: t });
    return {
      ...r,
      elGroep: el ? el.groep : null,
      elMwh: el ? el.mwh : null,
      elTarief: el ? el.tariefPerMwh : null,
      elCommissie: el ? el.commissie : null,
      injGroep: inj ? inj.groep : null,
      injMwh: inj ? inj.mwh : null,
      injTarief: inj ? inj.tariefPerMwh : null,
      injCommissie: inj ? inj.commissie : null,
      ngGroep: ng ? ng.groep : null,
      ngMwh: ng ? ng.mwh : null,
      ngTarief: ng ? ng.tariefPerMwh : null,
      ngCommissie: ng ? ng.commissie : null,
      commissie: (el ? el.commissie : 0) + (inj ? inj.commissie : 0) + (ng ? ng.commissie : 0),
    };
  });
}

// Optelling per verkoper én per prijszetting. Elke KANT telt mee in zijn eigen
// groep: een klant kan elektriciteit onder de custom pricing hebben en gas onder
// het vaste tarief, en die twee horen dan in verschillende regels.
export function evidentPerVerkoper(rijen) {
  const per = new Map();
  const sleutel = (v, g) => `${v}|${g}`;
  const zorg = (verkoper, groep) => {
    const k = sleutel(verkoper, groep);
    if (!per.has(k)) {
      per.set(k, {
        verkoper, groep, aantalEan: 0, commissie: 0,
        elMwh: 0, elCommissie: 0, injMwh: 0, injCommissie: 0, ngMwh: 0, ngCommissie: 0,
      });
    }
    return per.get(k);
  };
  const tel = (verkoper, groep, mwhVeld, comVeld, mwh, com) => {
    const p = zorg(verkoper, groep);
    p[mwhVeld] += mwh || 0;
    p[comVeld] += com || 0;
    p.commissie += com || 0;
    p.aantalEan += 1;
  };
  for (const r of rijen || []) {
    const verkoper = r.verkoper || '—';
    if (r.elGroep) tel(verkoper, r.elGroep, 'elMwh', 'elCommissie', r.elMwh, r.elCommissie);
    if (r.injGroep) tel(verkoper, r.injGroep, 'injMwh', 'injCommissie', r.injMwh, r.injCommissie);
    if (r.ngGroep) tel(verkoper, r.ngGroep, 'ngMwh', 'ngCommissie', r.ngMwh, r.ngCommissie);
  }
  const rows = [...per.values()].sort((a, b) =>
    b.commissie - a.commissie || a.verkoper.localeCompare(b.verkoper) || a.groep.localeCompare(b.groep));
  const totaal = rows.reduce((a, r) => ({
    elMwh: a.elMwh + r.elMwh, elCommissie: a.elCommissie + r.elCommissie,
    injMwh: a.injMwh + r.injMwh, injCommissie: a.injCommissie + r.injCommissie,
    ngMwh: a.ngMwh + r.ngMwh, ngCommissie: a.ngCommissie + r.ngCommissie,
    aantalEan: a.aantalEan + r.aantalEan, commissie: a.commissie + r.commissie,
  }), { elMwh: 0, elCommissie: 0, injMwh: 0, injCommissie: 0, ngMwh: 0, ngCommissie: 0, aantalEan: 0, commissie: 0 });
  return { rows, totaal };
}

// ── Evident: projectie op het nog te factureren volume ───────────────────────
// De commissiepagina toont wat er GEFACTUREERD is. Een verkoper wil ook weten wat
// er nog aan zit te komen: elke order draagt een geschat jaarvolume (Est. Offtake
// en Est. Injection), en wat daarvan nog niet gefactureerd is, levert straks nog
// commissie op.
//
// Rest = geschat volume − al gefactureerd, nooit negatief. Geen bruikbare
// schatting → geen projectie (liever niets beloven dan een verzonnen bedrag).

const projectieSleutel = (o) => `${String(o?.gsrn || '').trim()}|${String(o?.market || 'EL').toUpperCase() === 'NG' ? 'NG' : 'EL'}`;

// Eén order per aansluiting én energietype. Verlengingen en productwissels
// beschrijven DEZELFDE EAN; hun geschatte jaarvolumes optellen zou de projectie
// verdubbelen. We houden de meest recente die binnen de periode loopt.
export function evidentActieveOrders(orders, { van = '', tot = '' } = {}) {
  const per = new Map();
  for (const o of orders || []) {
    if (!String(o?.gsrn || '').trim()) continue;
    const start = String(o.start || '').slice(0, 10);
    const eind = String(o.eind || '').slice(0, 10);
    if (tot && start && start > tot) continue;   // begint pas ná de periode
    if (van && eind && eind < van) continue;     // al gestopt vóór de periode
    const k = projectieSleutel(o);
    const huidig = per.get(k);
    if (!huidig || String(huidig.start || '') <= start) per.set(k, o);
  }
  return [...per.values()];
}

// De projectie per order-kant (afname, injectie, gas).
//
//   gefactureerd : Map gsrn → { el, ng, inj } in kWh, uit de facturen/self bills
//   formule      : Map gsrn → { elMaj, ngMaj } — de opslag van de LAATSTE factuur.
//                  Een order zonder factuurgeschiedenis kent zijn prijsformule
//                  niet; valt die onder de custom pricing, dan rekenen we met het
//                  gemiddeld gerealiseerde tarief van die groep (`gemiddeld`) en
//                  markeren we de regel als geschat.
//   gemiddeld    : { custom: { el, ng, inj } } in €/MWh
export function evidentProjectie(orders, opties = {}) {
  const { tarieven = EVIDENT_DEFAULT_TARIEVEN, gefactureerd = new Map(), formule = new Map(), gemiddeld = {} } = opties;
  const t = normaliseerEvident(tarieven);
  const uit = [];
  for (const o of orders || []) {
    const gsrn = String(o.gsrn || '').trim();
    const markt = String(o.market || 'EL').toUpperCase() === 'NG' ? 'NG' : 'EL';
    const gef = gefactureerd.get(gsrn) || {};
    const f = formule.get(gsrn) || {};
    const kanten = markt === 'NG'
      ? [{ energie: 'NG', est: o.estOfftakeKwh, al: gef.ng, maj: f.ngMaj }]
      : [
        { energie: 'EL', est: o.estOfftakeKwh, al: gef.el, maj: f.elMaj },
        { energie: 'INJ', est: o.estInjectionKwh, al: gef.inj, maj: f.injMaj },
      ];
    for (const k of kanten) {
      const est = Number(k.est) || 0;
      if (est <= 0) continue;                       // geen schatting → geen projectie
      const rest = Math.max(0, est - (Number(k.al) || 0));
      if (rest <= 0) continue;                      // al volledig gefactureerd
      const groep = evidentGroep(o.segment, o.meterMethod, k.energie);
      const majBekend = k.energie === 'INJ' || (k.maj != null && k.maj !== '' && Number(k.maj) !== 0);
      let tariefPerMwh;
      let geschat = false;
      if (groep === 'custom' && !majBekend) {
        const g = gemiddeld.custom || {};
        tariefPerMwh = Number(g[k.energie === 'NG' ? 'ng' : k.energie === 'INJ' ? 'inj' : 'el']) || 0;
        geschat = true;
      } else {
        const z = evidentZijde({ kwh: rest, segment: o.segment, meterMethod: o.meterMethod, maj: k.maj, energie: k.energie, tarieven: t });
        tariefPerMwh = z ? z.tariefPerMwh : 0;
      }
      uit.push({
        gsrn,
        markt,
        energie: k.energie,
        verkoper: o.verkoper || '—',
        verkoopteam: o.verkoopteam || '',
        klant: o.klant || '',
        segment: o.segment || '',
        meterMethod: o.meterMethod || '',
        groep,
        estKwh: est,
        alKwh: Number(k.al) || 0,
        restKwh: rest,
        restMwh: rest / 1000,
        tariefPerMwh,
        geschatTarief: geschat,
        commissie: (rest / 1000) * tariefPerMwh,
      });
    }
  }
  return uit;
}

// Optelling van de projectie per verkoper.
export function evidentProjectiePerVerkoper(rijen) {
  const per = new Map();
  for (const r of rijen || []) {
    const p = per.get(r.verkoper) || { verkoper: r.verkoper, restMwh: 0, commissie: 0, aantal: 0, geschat: 0 };
    p.restMwh += r.restMwh;
    p.commissie += r.commissie;
    p.aantal += 1;
    if (r.geschatTarief) p.geschat += 1;
    per.set(r.verkoper, p);
  }
  return [...per.values()].sort((a, b) => b.commissie - a.commissie || a.verkoper.localeCompare(b.verkoper));
}

// Het gemiddeld gerealiseerde tarief per prijszetting en energiesoort (€/MWh),
// uit de al berekende EAN-regels. Dient als terugval voor orders zonder gekende
// prijsformule; zonder realisatie is er niets om op te steunen → 0.
export function evidentGemiddeldTarief(rijen) {
  const leeg = () => ({ el: { mwh: 0, com: 0 }, ng: { mwh: 0, com: 0 }, inj: { mwh: 0, com: 0 } });
  const acc = { res: leeg(), custom: leeg() };
  for (const r of rijen || []) {
    if (r.elGroep) { acc[r.elGroep].el.mwh += r.elMwh || 0; acc[r.elGroep].el.com += r.elCommissie || 0; }
    if (r.ngGroep) { acc[r.ngGroep].ng.mwh += r.ngMwh || 0; acc[r.ngGroep].ng.com += r.ngCommissie || 0; }
    if (r.injGroep) { acc[r.injGroep].inj.mwh += r.injMwh || 0; acc[r.injGroep].inj.com += r.injCommissie || 0; }
  }
  const deel = (x) => (x.mwh > 0 ? x.com / x.mwh : 0);
  return {
    res: { el: deel(acc.res.el), ng: deel(acc.res.ng), inj: deel(acc.res.inj) },
    custom: { el: deel(acc.custom.el), ng: deel(acc.custom.ng), inj: deel(acc.custom.inj) },
  };
}
